import { z } from 'zod'
import { oktaAuth } from '../config/okta'
import { ApiError, apiCall, publicApiCall } from './index'

// Backend authentication schemas
export const backendAuthSchema = z
  .object({
    access_token: z.string(), // Backend uses snake_case
    expires_in: z.number().optional(), // Token expiry time in seconds
    user: z.object({
      id: z.number(),
      email: z.string(),
      name: z.string(),
      type: z.enum(['okta', 'local']).optional().nullable(),
      roles: z.array(z.string()).optional().nullable(),
      permissions: z.array(z.string()).optional().nullable(),
    }),
  })
  .transform(data => ({
    // Transform to camelCase for frontend consistency
    success: true, // Add success field for compatibility
    accessToken: data.access_token,
    expiresIn: data.expires_in,
    user: data.user,
  }))

export const oktaCallbackSchema = z.object({
  code: z.string(),
  codeVerifier: z.string(),
  state: z.string().optional(),
})

export const userInfoSchema = z.object({
  user: z.object({
    id: z.number(),
    email: z.string(),
    name: z.string(),
    type: z.enum(['okta', 'local']),
    roles: z.array(z.string()),
    permissions: z.array(z.string()),
  }),
})

export const tokenRefreshSchema = z
  .object({
    access_token: z.string(),
    expires_in: z.number().optional(),
    user: z
      .object({
        id: z.number(),
        email: z.string(),
        name: z.string(),
        type: z.enum(['okta', 'local']).optional().nullable(),
        roles: z.array(z.string()).optional().nullable(),
        permissions: z.array(z.string()).optional().nullable(),
      })
      .optional(),
  })
  .transform(data => ({
    accessToken: data.access_token,
    expiresIn: data.expires_in,
    user: data.user,
  }))

// Types
export type BackendAuthResponse = z.infer<typeof backendAuthSchema>
export type OktaCallbackRequest = z.infer<typeof oktaCallbackSchema>
export type UserInfo = z.infer<typeof userInfoSchema>
export type TokenRefreshResponse = z.infer<typeof tokenRefreshSchema>

// In-memory token management (secure against XSS compared to localStorage)
let currentAccessToken: string | null = null
let currentUser: BackendAuthResponse['user'] | null = null

export function setAccessToken(token: string) {
  currentAccessToken = token
}

export async function getAccessToken(): Promise<string | null> {
  // If access token exists, return it
  if (currentAccessToken) {
    return currentAccessToken
  }
  // If access token is missing, try to refresh using refresh token
  try {
    const refreshData = await refreshAccessToken()
    return refreshData.accessToken
  } catch (error) {
    console.warn('Failed to refresh access token:', error)
    return null
  }
}

export function clearAccessToken() {
  currentAccessToken = null
}

// User data management (secure in-memory storage)
export function setCurrentUser(user: BackendAuthResponse['user']) {
  currentUser = user
}

export function getCurrentUserData(): BackendAuthResponse['user'] | null {
  return currentUser
}

export function clearCurrentUser() {
  currentUser = null
}

/**
 * Helper functions to get specific user data for API calls
 *
 * These functions provide a centralized, secure way to access the current user's
 * information for use in API requests (e.g., rewardedById, rewardedByName).
 * @throws {Error} If user is not authenticated or user data is not available
 */
export function getCurrentUserId(): number {
  if (!currentUser) {
    throw new Error('User data not available. Please ensure user is authenticated.')
  }
  return currentUser.id
}

export function getCurrentUserName(): string {
  if (!currentUser) {
    throw new Error('User data not available. Please ensure user is authenticated.')
  }
  return currentUser.name
}

export function getCurrentUserEmail(): string {
  if (!currentUser) {
    throw new Error('User data not available. Please ensure user is authenticated.')
  }
  return currentUser.email
}

// Check if user is authenticated with access token
export async function isAuthenticated(): Promise<boolean> {
  const token = await getAccessToken()

  if (!token) return false

  try {
    // Basic JWT expiry check (without full verification)
    const payload = JSON.parse(atob(token.split('.')[1]))
    const now = Math.floor(Date.now() / 1000)

    return payload.exp > now
  } catch {
    return false
  }
}

// Generate PKCE code verifier and challenge
function generateCodeVerifier(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return btoa(String.fromCharCode.apply(null, Array.from(array)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(digest))))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

// Okta login flow with custom PKCE handling
export async function oktaLogin(): Promise<void> {
  try {
    // Generate PKCE parameters
    const codeVerifier = generateCodeVerifier()
    const codeChallenge = await generateCodeChallenge(codeVerifier)

    // Store code verifier for later use in callback
    sessionStorage.setItem('oktaCodeVerifier', codeVerifier)

    // Start Okta OAuth flow with PKCE
    await oktaAuth.signInWithRedirect({
      originalUri: window.location.pathname,
      codeChallenge,
      codeChallengeMethod: 'S256',
    })
  } catch (error) {
    console.error('Failed to initiate Okta login:', error)
    throw new Error('Failed to start Okta authentication')
  }
}

// Handle Okta callback by sending auth code to backend
export async function handleOktaCallback(): Promise<BackendAuthResponse> {
  try {
    // Check if we're in a callback flow
    if (!oktaAuth.isLoginRedirect()) {
      throw new Error('Not in an Okta callback flow')
    }

    // Get authorization code from URL for backend exchange
    const urlParams = new URLSearchParams(window.location.search)
    const authCode = urlParams.get('code')

    if (!authCode) {
      throw new Error('No authorization code received from Okta')
    }

    // Retrieve the stored code verifier
    const codeVerifier = sessionStorage.getItem('oktaCodeVerifier')
    if (!codeVerifier) {
      throw new Error('PKCE code verifier not found - login may have been initiated elsewhere')
    }

    // Clear the stored code verifier
    sessionStorage.removeItem('oktaCodeVerifier')

    // Send auth code and code verifier to backend - backend handles token exchange
    const response = await publicApiCall('/v1/auth/okta/exchange', {
      method: 'POST',
      credentials: 'include', // Important: includes cookies for refresh token
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code: authCode,
        codeVerifier: codeVerifier,
        redirectUri: import.meta.env.VITE_OKTA_REDIRECT_URI,
      }),
      signal: AbortSignal.timeout(60000),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Authentication failed' }))
      throw new Error(errorData.error || 'Backend authentication failed')
    }

    const data = await response.json()
    const parsedData = backendAuthSchema.parse(data)

    // Store access token and user data in memory (refresh token is in HttpOnly cookie)
    setAccessToken(parsedData.accessToken)
    setCurrentUser(parsedData.user)

    return parsedData
  } catch (error) {
    console.error('Okta callback failed:', error)
    if (error instanceof ApiError) {
      throw error
    }
    throw new Error('Failed to complete Okta authentication')
  }
}

// Refresh access token using HttpOnly cookie
export async function refreshAccessToken(): Promise<TokenRefreshResponse> {
  try {
    const response = await publicApiCall('/v1/auth/refresh', {
      method: 'POST',
      credentials: 'include', // Includes refresh token cookie
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Token refresh failed' }))
      throw new Error(errorData.error || 'Token refresh failed')
    }

    const data = await response.json()
    const parsedData = tokenRefreshSchema.parse(data)

    // Update access token and user data in memory
    setAccessToken(parsedData.accessToken)
    if (parsedData.user) {
      setCurrentUser(parsedData.user)
    }

    return parsedData
  } catch (error) {
    console.error('Token refresh failed:', error)
    clearAccessToken()
    clearCurrentUser()
    throw error
  }
}

// Get current user info
export async function getCurrentUser(): Promise<UserInfo> {
  try {
    const token = await getAccessToken()
    if (!token) {
      throw new Error('No access token available')
    }

    const response = await apiCall('/v1/auth/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to get user info' }))
      throw new Error(errorData.error || 'Failed to get user info')
    }

    const data = await response.json()
    return userInfoSchema.parse(data)
  } catch (error) {
    console.error('Failed to get user info:', error)
    throw error
  }
}

// Logout (clears tokens and notifies backend)
export async function logout(): Promise<void> {
  try {
    // Notify backend to revoke refresh token
    try {
      await apiCall('/v1/auth/logout', {
        method: 'POST',
        credentials: 'include', // Includes refresh token cookie
      })
    } catch (error) {
      console.warn('Backend logout notification failed:', error)
      // Don't throw error, continue with local logout
    }

    // Clear local access token and user data
    clearAccessToken()
    clearCurrentUser()

    // Also logout from Okta to clear Okta session
    try {
      await oktaAuth.signOut()
    } catch (error) {
      console.warn('Okta logout failed:', error)
      // Don't throw error, local logout is more important
    }
  } catch (error) {
    console.error('Logout failed:', error)
    throw error
  }
}

// Handle IdP-initiated login (from Okta tile)
export async function handleIdPInitiated(): Promise<BackendAuthResponse | null> {
  try {
    // Check if this is an IdP-initiated flow
    const urlParams = new URLSearchParams(window.location.search)
    const isIdPFlow = urlParams.has('iss') || urlParams.has('fromURI') || oktaAuth.isLoginRedirect()

    if (!isIdPFlow) {
      return null
    }

    // If already authenticated, return current user
    if (await isAuthenticated()) {
      const userInfo = await getCurrentUser()
      const accessToken = await getAccessToken()
      return {
        success: true,
        accessToken: accessToken!,
        expiresIn: 3600,
        user: userInfo.user,
      }
    }

    // Handle the IdP-initiated authentication
    if (oktaAuth.isLoginRedirect()) {
      return await handleOktaCallback()
    } else {
      // Start authentication flow
      await oktaLogin()
      return null // Will redirect to Okta
    }
  } catch (error) {
    console.error('IdP-initiated authentication failed:', error)
    throw error
  }
}

// Check if current request is coming from Okta tile (IdP-initiated)
export function isIdPInitiated(): boolean {
  const urlParams = new URLSearchParams(window.location.search)
  // Common indicators of IdP-initiated login
  return (
    urlParams.has('iss') || // Okta issuer parameter
    urlParams.has('fromURI') || // Okta fromURI parameter
    urlParams.has('client_id') || // OIDC client_id parameter
    oktaAuth.isLoginRedirect() // Okta SDK check
  )
}

// Ensure valid access token (refresh if needed)
export async function ensureValidAccessToken(): Promise<string> {
  let token = await getAccessToken()

  if (!token) {
    // Try to refresh token first
    try {
      const refreshData = await refreshAccessToken()
      token = refreshData.accessToken
    } catch {
      throw new Error('No authentication token available')
    }
  }

  // Check if current token is still valid
  if (!(await isAuthenticated())) {
    try {
      const refreshData = await refreshAccessToken()
      token = refreshData.accessToken
    } catch {
      throw new Error('Authentication token expired and refresh failed')
    }
  }

  return token
}
